import { DatePicker } from "antd";
import dayjs from "dayjs";
import { forwardRef, memo, useId } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/classNames";

export const DATE_FORMATS_BY_LOCALE = {
  en: "YYYY-MM-DD",
  it: "DD/MM/YYYY",
  fr: "DD/MM/YYYY",
  de: "DD.MM.YYYY",
};

export const getDateFormatByLocale = (locale?: string) => {
  if (!locale) return DATE_FORMATS_BY_LOCALE.en;

  const language = locale.split("-")[0];
  return DATE_FORMATS_BY_LOCALE[language as keyof typeof DATE_FORMATS_BY_LOCALE] || DATE_FORMATS_BY_LOCALE.en;
};

const CustomSharedDatePicker = forwardRef<any, any>(
  (
    {
      label,
      customLabel,
      labelClassName,
      containerClassName = "",
      className,
      format,
      id,
      isFutureDateAvailable = true,
      isPastDateAvailable = true,
      disabledDate: customDisabledDate,
      ...restProps
    },
    ref,
  ) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const renderedLabel = customLabel ?? label;
    const generatedId = useId();
    const pickerId = id ?? generatedId;
    const defaultFormat = restProps.picker
      ? undefined
      : getDateFormatByLocale(currentLang);

    const resolvedDisabledDate = (currentDate: any) => {
      const isFutureDateDisabled =
        !isFutureDateAvailable && currentDate && currentDate > dayjs().endOf("day");
      const isPastDateDisabled =
        !isPastDateAvailable && currentDate && currentDate < dayjs().startOf("day");
      const isCustomDateDisabled = customDisabledDate?.(currentDate);

      return isFutureDateDisabled || isPastDateDisabled || isCustomDateDisabled;
    };

    return (
      <div className={cn("flex flex-col", containerClassName)}>
        {renderedLabel ? (
          <label
            className={cn("block text-sm mb-2 text-gray9", labelClassName)}
            htmlFor={pickerId}
          >
            {renderedLabel}
          </label>
        ) : null}
        <DatePicker
          ref={ref}
          className={cn("custom-date-picker w-full", className)}
          id={pickerId}
          {...restProps}
          format={format ?? defaultFormat}
          disabledDate={
            isFutureDateAvailable && isPastDateAvailable && !customDisabledDate
              ? undefined
              : resolvedDisabledDate
          }
        />
      </div>
    );
  },
);

CustomSharedDatePicker.displayName = "CustomSharedDatePicker";

export default memo(CustomSharedDatePicker);
